import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CreditCard, Calendar, Banknote, Smartphone, Wallet, CheckCircle } from "lucide-react";

const PaymentMethodModal = ({
  open,
  onOpenChange,
  pendingValidationInvoice,
  invoiceDate,
  setInvoiceDate,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  onCancel,
  onConfirm,
  formatPrice,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-slate-800 border-slate-700 text-white rounded-2xl max-h-[92vh] overflow-y-auto p-4 sm:p-6"
        style={{ width: "min(calc(100vw - 1.5rem), 28rem)" }}
        data-testid="payment-method-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-400">
            <CreditCard className="w-5 h-5" />
            Transformer en Bon-Client
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Sélectionnez le mode de paiement pour la facture {pendingValidationInvoice?.invoice_number}
          </DialogDescription>
        </DialogHeader>

        {pendingValidationInvoice && (
          <div className="space-y-4">
            {/* Invoice Summary */}
            <div className="bg-slate-900/50 rounded-lg p-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Client:</span>
                <span className="text-white">{pendingValidationInvoice.customer_name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Agent:</span>
                <span className="text-white">{pendingValidationInvoice.created_by}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Articles:</span>
                <span className="text-white">{pendingValidationInvoice.items?.length || 0}</span>
              </div>
              <div className="flex justify-between font-bold text-lg border-t border-slate-700 pt-2 mt-2">
                <span className="text-slate-300">Total:</span>
                <span className="text-green-400">{formatPrice(pendingValidationInvoice.total)} F</span>
              </div>
            </div>

            {/* Invoice Date Selection */}
            <div className="space-y-2">
              <Label className="text-slate-300 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Date de facturation
              </Label>
              <Input
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                className="bg-slate-900 border-slate-600 text-white"
                data-testid="payment-invoice-date"
              />
            </div>

            {/* Payment Method Selection */}
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Mode de paiement</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Button
                  type="button"
                  variant={selectedPaymentMethod === "cash" ? "default" : "outline"}
                  onClick={() => setSelectedPaymentMethod("cash")}
                  className={`flex flex-col items-center py-2.5 sm:py-4 h-auto ${
                    selectedPaymentMethod === "cash"
                      ? "bg-green-600 hover:bg-green-700 border-green-500"
                      : "border-slate-600 hover:bg-slate-700"
                  }`}
                  data-testid="pay-method-cash"
                >
                  <Banknote className="w-5 h-5 sm:w-6 sm:h-6 mb-0.5 sm:mb-1" />
                  <span className="text-[11px] sm:text-xs">Espèces</span>
                </Button>
                <Button
                  type="button"
                  variant={selectedPaymentMethod === "mobile" ? "default" : "outline"}
                  onClick={() => setSelectedPaymentMethod("mobile")}
                  className={`flex flex-col items-center py-2.5 sm:py-4 h-auto ${
                    selectedPaymentMethod === "mobile"
                      ? "bg-orange-600 hover:bg-orange-700 border-orange-500"
                      : "border-slate-600 hover:bg-slate-700"
                  }`}
                  data-testid="pay-method-mobile"
                >
                  <Smartphone className="w-5 h-5 sm:w-6 sm:h-6 mb-0.5 sm:mb-1" />
                  <span className="text-[11px] sm:text-xs">Mobile Money</span>
                </Button>
                <Button
                  type="button"
                  variant={selectedPaymentMethod === "card" ? "default" : "outline"}
                  onClick={() => setSelectedPaymentMethod("card")}
                  className={`flex flex-col items-center py-2.5 sm:py-4 h-auto ${
                    selectedPaymentMethod === "card"
                      ? "bg-blue-600 hover:bg-blue-700 border-blue-500"
                      : "border-slate-600 hover:bg-slate-700"
                  }`}
                  data-testid="pay-method-card"
                >
                  <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 mb-0.5 sm:mb-1" />
                  <span className="text-[11px] sm:text-xs">Carte</span>
                </Button>
                <Button
                  type="button"
                  variant={selectedPaymentMethod === "kkiapay" ? "default" : "outline"}
                  onClick={() => setSelectedPaymentMethod("kkiapay")}
                  className={`flex flex-col items-center py-2.5 sm:py-4 h-auto ${
                    selectedPaymentMethod === "kkiapay"
                      ? "bg-pink-600 hover:bg-pink-700 border-pink-500"
                      : "border-slate-600 hover:bg-slate-700"
                  }`}
                  data-testid="pay-method-kkiapay"
                >
                  <Wallet className="w-5 h-5 sm:w-6 sm:h-6 mb-0.5 sm:mb-1" />
                  <span className="text-[11px] sm:text-xs">KKiapay</span>
                </Button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={onCancel}
                className="flex-1 border-slate-600"
                data-testid="payment-cancel-btn"
              >
                Annuler
              </Button>
              <Button
                onClick={onConfirm}
                className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                data-testid="payment-confirm-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Valider
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PaymentMethodModal;
